// Modules
import { useEffect, useState, useReducer } from 'react';
import { toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

// Components
import { StyledTitle, FilterRowWrapper, ButtonPrimary } from '../../common/components';
import { FAQManagementGrid } from './FAQManagementGrid';
import { FAQManagementWrapper } from './FAQManagementStyles';

// Utils
import { gridReducer, GRID_ACTIONS, IGridReducerState } from '../../reducer/gridReducer/gridReducer';

// Modals
import { BlankModal } from '../mymessages/modalTemplates/BlankModal';
import { ConfirmationModal } from '../mymessages/modalTemplates/ConfirmationModal';
import { FAQManagementModal } from './FAQManagementModal';

import { createFAQ, deleteFAQ, getAdminFAQs, publishFAQ, updateFAQ } from '../../common/api/adminFaqApi';
import { globalApiParams } from '../../common/api/globals';
import { FAQPreviewModal } from './FAQPreviewModal';

export const FAQManagement = () => {
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showPublishFaqModal, setShowPublishFaqModal] = useState<boolean>(false);
  const [showIsPublishedFaqModal, setShowIsPublishedFaqModal] = useState<boolean>(false);
  const [showDeleteFaqModal, setShowDeleteFaqModal] = useState<boolean>(false);
  const [showIsDeletedFaqModal, setShowIsDeletedFaqModal] = useState<boolean>(false);
  const [isPublishEnabled, setIsPublishEnabled] = useState<boolean>(false);

  const { instance, accounts } = useMsal();
  const username = accounts[0] && accounts[0].username;

  const initialState: IGridReducerState = {
    page: 1,
    currentType: '',
    data: {},
    gridData: [],
    initialSortField: 'startDate',
    initialSortOrder: 'DESC',
    currentSortField: 'startDate',
    currentSortOrder: 'DESC',
    isLoading: false,
    hasMoreData: true,
    selectedCustomerId: '-1',
  };

  const [state, dispatch] = useReducer(gridReducer, initialState);

  const error = (error: string) => {
    const message = `API error calling: ${error}`;
    console.error(message);
    toast.error(message, { position: 'bottom-right' });
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
  };

  const adminFAQsSuccess = (result: Common.IResult<Array<Api.IAdminFAQPagedData>>) => {
    if (result && result.success) {
      dispatch({
        type: GRID_ACTIONS.ADD_DATA,
        payload: { data: result.data, page: state.page },
      });
    } else {
      error('getAdminFAQs');
    }

    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
  };

  const loadData = async () => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const params: Api.IAdminFAQGridParams = {
      page: state.page,
      pageSize: globalApiParams.pageSize,
      sortDir: state.currentSortOrder,
    };

    getAdminFAQs(accounts[0], instance, params, adminFAQsSuccess, error);
  };

  useEffect(() => {
    loadData();
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentSortField, state.currentSortOrder, state.page]);

  const handleSortChange = (sort: Common.ISort) => {
    setSelectedRowIndex(-1);
    dispatch({ type: GRID_ACTIONS.CHANGE_SORT, payload: sort });
  };

  const handlePagingChange = () => {
    dispatch({ type: GRID_ACTIONS.INCREMENT_PAGE });
  };

  const handleSelectedRowChange = (index: number, item: any) => {
    setSelectedRowIndex(index);
    setSelectedRow(item);
  };

  const deleteFaqSuccess = (result: Common.IResult<Array<boolean>>) => {
    if (result && result.success) {
      // Reload grid
      loadData();
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
      setShowIsDeletedFaqModal(true);
    } else {
      error('deletefaq');
    }
  };

  const handleDelete = (row: any) => {
    setSelectedRowIndex(-1);
    setShowDeleteFaqModal(true);
  };

  const handleDeleteFaqYes = () => {
    setShowDeleteFaqModal(false);

    const row = selectedRow;

    if (row) {
      deleteFAQ(accounts[0], instance, row.id, deleteFaqSuccess, error);
    }
  };

  const handlePreviewFaq = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (state.gridData && selectedRowIndex >= 0) {
      setShowPreviewModal(true);
    }
  };

  const handleNewFaq = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setSelectedRowIndex(-1);
    setShowAddNewModal(true);
  };

  const handleCreateClick = async (faq: Api.IFAQRequest) => {
    await createFAQ(accounts[0], faq, instance, createFaqSuccess, error);
  };

  const createFaqSuccess = (result: Common.IResult<Array<boolean>>) => {
    if (result && result.success) {
      // Reload grid
      loadData();
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
      setSelectedRowIndex(-1);
    } else {
      error('createFaq');
    }
  };

  const handleUpdateClick = async (faq: any) => {
    await updateFAQ(accounts[0], faq, instance, updateFaqSuccess, error);
  };

  const updateFaqSuccess = (result: Common.IResult<Array<boolean>>) => {
    if (result && result.success) {
      // Reload grid
      loadData();
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
      setSelectedRowIndex(-1);
    } else {
      error('updateFaq');
    }
  };

  const publishFaqSuccess = (result: Common.IResult<Array<boolean>>) => {
    if (result && result.success) {
      // Reload grid
      loadData();
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
      setShowIsPublishedFaqModal(true);
    } else {
      error('publishFaq');
    }
  };

  const handlePublishFaqYes = () => {
    setShowPublishFaqModal(false);

    const row = selectedRow;
    if (row) {
      setIsPublishEnabled(false);
      publishFAQ(accounts[0], instance, row.id, publishFaqSuccess, error);
    }
  };

  const handlePublish = (event: any) => {
    const row = selectedRow;
    setShowPublishFaqModal(true);
  };

  const handleDownloadGenericDocument = async (id: number) => {
    const request = {
      ...loginRequest,
      account: accounts[0],
    };

    instance
      .acquireTokenSilent(request)
      .then(async (response: any) => {
        var headers = new Headers();
        var bearer = 'Bearer ' + response.idToken;
        headers.append('Authorization', bearer);
        var options = {
          method: 'GET',
          headers: headers,
          responseType: 'blob',
        };

        const url = process.env.REACT_APP_API_SERVICES_URL;

        const downloadGenericDocumentAdminEndpoint = `${url}/documentmanagement/getfaqdocument/${id}`;

        fetch(downloadGenericDocumentAdminEndpoint, options).then(async response => {
          if (response.status === 200) {
            let contentDisposition = response.headers.get('content-disposition');
            let fileName = contentDisposition?.split('filename=')[1].split(';')[0];
            // remove outer quotes
            fileName = fileName?.replace(/"/g, '');

            const downloadUrl = window.URL.createObjectURL(await response.blob());
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName!);
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        });
      })
      .catch((e: any) => {
        const message = 'API error calling: downloadGenericDocumentAdmin';
        console.error(message);
        toast.error(message, { position: 'bottom-right' });
      });
  };

  useEffect(() => {
    const enabledPublish =
      selectedRowIndex >= 0 &&
      (selectedRow.status === 'Ready' || selectedRow.status === 'Expired') &&
      selectedRow.updatedBy !== username;

    setIsPublishEnabled(enabledPublish);
  }, [selectedRow]);

  const isPreviewEnabled = selectedRowIndex >= 0;

  const isUpdateEnabled =
    selectedRowIndex >= 0 && selectedRow.status !== 'Published' && selectedRow.status !== 'Expired';

  const selectedItem: Api.IFAQRequest = {
    file: selectedRow >= 0 ? selectedRow?.file : null,
    format: selectedRowIndex >= 0 ? selectedRow?.format : '',
    fileName: selectedRowIndex >= 0 ? selectedRow?.fileName : '',
    faqAudienceId: selectedRowIndex >= 0 ? selectedRow?.faqAudienceId : '',
    faqCategoryId: selectedRowIndex >= 0 ? selectedRow?.faqCategoryId : '',
    internalOnly: false,
    priority: selectedRowIndex >= 0 ? selectedRow?.priority : '',
    question: selectedRowIndex >= 0 ? selectedRow?.question : '',
    answer: selectedRowIndex >= 0 ? selectedRow?.answer : '',
    supportingLink: selectedRowIndex >= 0 ? selectedRow?.supportingLink : '',
    imageSupportingText: selectedRowIndex >= 0 ? selectedRow?.imageSupportingText : '',
    statusId: selectedRowIndex >= 0 ? selectedRow?.statusId : '',
    storageKey: selectedRowIndex >= 0 ? selectedRow?.storageKey : '',
    id: selectedRowIndex >= 0 ? selectedRow?.faqId : '',
    faqDocumentId: selectedRowIndex >= 0 ? selectedRow?.faqDocumentId : null,
  };

  return (
    <FAQManagementWrapper>
      <div className='adminFaqRepository container-fluid'>
        <div className='row'>
          <div className='col-md-12'>
            <FilterRowWrapper>
              <StyledTitle>FAQ management</StyledTitle>
              <div className='d-flex justify-content-end'>
                <ButtonPrimary title='Preview' onClick={handlePreviewFaq} disabled={!isPreviewEnabled} />
                <ButtonPrimary className='ml-3' title='New FAQ' onClick={handleNewFaq} />
                <ButtonPrimary
                  className='ml-3'
                  title='Update'
                  onClick={() => setShowEditModal(true)}
                  disabled={!isUpdateEnabled}
                />
                <ButtonPrimary className='ml-3' title='Publish' onClick={handlePublish} disabled={!isPublishEnabled} />
              </div>
            </FilterRowWrapper>
          </div>
        </div>
      </div>

      <FAQManagementGrid
        gridData={state.gridData}
        isLoading={state.isLoading}
        hasMoreData={state.hasMoreData}
        selectedRowIndex={selectedRowIndex}
        handleSortChange={handleSortChange}
        handlePagingChange={handlePagingChange}
        handleSelectedRowChange={handleSelectedRowChange}
        handleDelete={handleDelete}
        currentSortField={state.currentSortField}
        currentSortOrder={state.currentSortOrder}
      />

      <FAQPreviewModal
        show={showPreviewModal}
        item={selectedRow}
        onClose={() => {
          setShowPreviewModal(false);
        }}
      ></FAQPreviewModal>

      <FAQManagementModal
        show={showAddNewModal}
        edit={false}
        handleDownloadGenericDocument={handleDownloadGenericDocument}
        onSubmit={(isAccepted: boolean, item: Api.IFAQRequest) => {
          setShowAddNewModal(false);
          if (isAccepted) {
            handleCreateClick(item);
          }
        }}
        onClose={() => {
          setShowAddNewModal(false);
        }}
      ></FAQManagementModal>

      {showEditModal && (
        <FAQManagementModal
          show={showEditModal}
          edit={true}
          handleDownloadGenericDocument={handleDownloadGenericDocument}
          onSubmit={(isAccepted: boolean, item: Api.IFAQRequest) => {
            setShowEditModal(false);

            if (isAccepted) {
              var newItem: Api.IFAQRequest = {
                file: item.file,
                format: item.format,
                fileName: item.fileName,
                faqAudienceId: item.faqAudienceId,
                internalOnly: item.internalOnly,
                faqCategoryId: item.faqCategoryId,
                priority: item.priority,
                question: item.question,
                answer: item.answer,
                supportingLink: item.supportingLink,
                imageSupportingText: item.imageSupportingText,
                statusId: item.statusId,
                storageKey: item.storageKey,
                id: selectedRow.id,
                faqId: selectedRow.faqId,
              };

              handleUpdateClick(newItem);
            }
          }}
          onClose={() => {
            setShowEditModal(false);
          }}
          item={selectedItem}
        ></FAQManagementModal>
      )}

      <ConfirmationModal
        show={showPublishFaqModal}
        onHide={() => {
          setShowPublishFaqModal(false);
        }}
        onYes={() => {
          handlePublishFaqYes();
        }}
        onNo={() => {
          setShowPublishFaqModal(false);
        }}
        titleText={'Do you want to publish the faq?'}
      ></ConfirmationModal>

      <BlankModal
        show={showIsPublishedFaqModal}
        onHide={() => {
          setShowIsPublishedFaqModal(false);
        }}
        titleText={'Faq is successfully published in the portal.'}
        buttonText={'OK'}
      ></BlankModal>

      <ConfirmationModal
        show={showDeleteFaqModal}
        onHide={() => {
          setShowDeleteFaqModal(false);
        }}
        onYes={() => {
          handleDeleteFaqYes();
        }}
        onNo={() => {
          setShowDeleteFaqModal(false);
        }}
        titleText={'Do you want to delete the faq?'}
      ></ConfirmationModal>

      <BlankModal
        show={showIsDeletedFaqModal}
        onHide={() => {
          setShowIsDeletedFaqModal(false);
        }}
        titleText={'Faq is deleted from the admin page and the portal.'}
        buttonText={'OK'}
      ></BlankModal>
    </FAQManagementWrapper>
  );
};
